import { throwUa } from "./errors";
import { supabase } from "./supabase";

// Telegram Bot API upload limit.
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

export interface MessagePartner {
  id: string;
  name: string | null;
  avatar_url: string | null;
  telegram_username: string | null;
}

export interface MessageBatch {
  batch_id: string;
  body: string | null;
  created_at: string;
  recipient_ids: string[];
}

export interface SendMessageResult {
  ok: boolean;
  notified: number;
  delivered: string[];
  failed: { user_id: string; reason: string }[];
}

export const fetchMessagePartners = async (): Promise<MessagePartner[]> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, avatar_url, telegram_links!inner(telegram_username, linked)")
    .eq("role", "partner")
    .eq("telegram_links.linked", true)
    .order("name", { ascending: true });
  if (error) throwUa(error);
  return (data ?? []).map((row: any) => {
    const link = Array.isArray(row.telegram_links) ? row.telegram_links[0] : row.telegram_links;
    return {
      id: row.id,
      name: row.name,
      avatar_url: row.avatar_url,
      telegram_username: link?.telegram_username ?? null,
    };
  });
};

// Messages sent to any of `userIds`, one entry per send (batch), oldest first.
export const fetchMessageHistory = async (userIds: string[]): Promise<MessageBatch[]> => {
  if (userIds.length === 0) return [];
  const { data, error } = await supabase
    .from("notifications")
    .select("user_id, body, batch_id, created_at")
    .eq("type", "message")
    .not("batch_id", "is", null)
    .in("user_id", userIds)
    .order("created_at", { ascending: true })
    .limit(1000);
  if (error) throwUa(error);

  const batches = new Map<string, MessageBatch>();
  for (const row of (data ?? []) as any[]) {
    const existing = batches.get(row.batch_id);
    if (existing) {
      existing.recipient_ids.push(row.user_id);
      continue;
    }
    batches.set(row.batch_id, {
      batch_id: row.batch_id,
      body: row.body,
      created_at: row.created_at,
      recipient_ids: [row.user_id],
    });
  }
  return Array.from(batches.values());
};

// Text is stored as in-app notifications; files are forwarded to Telegram by
// the edge function and never stored.
export const sendPartnerMessage = async (params: {
  userIds: string[];
  text: string;
  files: File[];
}): Promise<SendMessageResult & { batch: MessageBatch }> => {
  const batchId = crypto.randomUUID();

  const form = new FormData();
  form.append("batch_id", batchId);
  form.append("user_ids", JSON.stringify(params.userIds));
  form.append("text", params.text);
  params.files.forEach((f) => form.append("files", f, f.name));

  const { data, error } = await supabase.functions.invoke<SendMessageResult>("send-message", {
    body: form,
  });
  if (error || !data) throwUa(error);

  return {
    ...(data as SendMessageResult),
    batch: {
      batch_id: batchId,
      body: params.text.trim() || `📂 Медіа: ${params.files.length}`,
      created_at: new Date().toISOString(),
      recipient_ids: params.userIds,
    },
  };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};
